import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  UpsertSchedulerUserSettingInput,
  useGetSchedulerReferenceUsersQuery,
  useGetSchedulerUserSettingsQuery,
  useUpsertSchedulerUserSettingMutation,
} from '@/app/services/serviceScheduler';
import { addToast } from '@/app/slices/toast';
import { InputText } from '@/components/common/service-scheduler/compat-controls';
import {
  SettingsItem,
  SettingsList,
} from '@/components/common/service-scheduler/settings-list';
import UserSettingsForm from '@/components/common/service-scheduler/user-settings-form';

export default function UserSettingsPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetSchedulerReferenceUsersQuery({
    activeOnly: true,
    startRow: 1,
    endRow: 500,
  });
  const { data: settings = [] } = useGetSchedulerUserSettingsQuery();
  const [upsertSetting] = useUpsertSchedulerUserSettingMutation();

  const settingsByUserId = useMemo(
    () => new Map(settings.map((s) => [s.userId.toLowerCase(), s])),
    [settings],
  );

  const filteredUsers = (usersData?.items ?? []).filter((u) =>
    u.name.toLowerCase().includes(query.toLowerCase().trim()),
  );

  const items: SettingsItem[] = filteredUsers.map((u) => {
    const current =
      settingsByUserId.get(u.equipmentId.toLowerCase()) ??
      (u.systemUserId
        ? settingsByUserId.get(u.systemUserId.toLowerCase())
        : undefined);
    return {
      id: u.equipmentId,
      title: u.name,
      icon: 'pi-user',
      showCheckbox: true,
      checked: current?.visible ?? true,
      onCheckChange: async (checked) => {
        const input: UpsertSchedulerUserSettingInput = {
          userId: u.equipmentId,
          calendarName: current?.calendarName ?? u.name,
          region: current?.region ?? null,
          siteCode: current?.siteCode ?? null,
          colorHex: current?.colorHex ?? '#0096ff',
          visible: checked,
        };
        try {
          await upsertSetting(input).unwrap();
        } catch {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('common.error'),
              detail: t('common.error'),
              sticky: true,
            }),
          );
        }
      },
      content: (
        <UserSettingsForm
          key={`${u.equipmentId}:${current?.calendarName ?? ''}:${current?.region ?? ''}:${current?.siteCode ?? ''}:${current?.colorHex ?? ''}`}
          user={{
            name: u.name,
            calendarName: current?.calendarName ?? '',
            region: current?.region ?? '',
            site: current?.siteCode ?? '',
            color: current?.colorHex ?? '#0096ff',
          }}
          onSave={async (updated) => {
            const input: UpsertSchedulerUserSettingInput = {
              userId: u.equipmentId,
              calendarName: updated.calendarName ?? null,
              region: updated.region ?? null,
              siteCode: updated.site ?? null,
              colorHex: updated.color ?? null,
              visible: current?.visible ?? true,
            };
            try {
              await upsertSetting(input).unwrap();
              dispatch(
                addToast({
                  severity: 'success',
                  summary: t('common.save'),
                  detail: t('common.save'),
                }),
              );
            } catch {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: t('common.error'),
                  detail: t('common.error'),
                  sticky: true,
                }),
              );
            }
          }}
        />
      ),
    };
  });

  if (usersError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('common.error')}: Failed to load users.
      </div>
    );
  }

  if (isLoadingUsers) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <>
      <span className="p-input-icon-right mb-2 w-full">
        <InputText
          placeholder={t('common.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full"
        />
      </span>

      {items.length === 0 && query === '' ? (
        <div className="py-4 text-center text-sm text-gray-500">
          No users found.
        </div>
      ) : null}

      <SettingsList items={items} multiple />
    </>
  );
}
